import * as React from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Button,
  Stack,
  Tooltip,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';

// Browser -> local offset in HOURS (e.g., +2)
export const defaultUtcOffset = () => -new Date().getTimezoneOffset() / 60;

type Props = {
  value?: number;                    // hours, e.g. 2, 3.5, 5.75
  onChange: (offsetHours: number) => void;
  disabled?: boolean;
};

const OFFSETS: { value: number; label: string }[] = [
  { value: -12, label: 'UTC−12:00' },
  { value: -11, label: 'UTC−11:00' },
  { value: -10, label: 'UTC−10:00' },
  { value: -9, label: 'UTC−09:00' },
  { value: -8, label: 'UTC−08:00' },
  { value: -7, label: 'UTC−07:00' },
  { value: -6, label: 'UTC−06:00' },
  { value: -5, label: 'UTC−05:00' },
  { value: -4, label: 'UTC−04:00' },
  { value: -3.5, label: 'UTC−03:30 (Newfoundland)' },
  { value: -3, label: 'UTC−03:00' },
  { value: -2, label: 'UTC−02:00' },
  { value: -1, label: 'UTC−01:00' },
  { value: 0, label: 'UTC±00:00' },
  { value: 1, label: 'UTC+01:00' },
  { value: 2, label: 'UTC+02:00' },
  { value: 3, label: 'UTC+03:00' },
  { value: 3.5, label: 'UTC+03:30 (Iran)' },
  { value: 4, label: 'UTC+04:00' },
  { value: 4.5, label: 'UTC+04:30 (Afghanistan)' },
  { value: 5, label: 'UTC+05:00' },
  { value: 5.5, label: 'UTC+05:30 (India, Sri Lanka)' },
  { value: 5.75, label: 'UTC+05:45 (Nepal)' },
  { value: 6, label: 'UTC+06:00' },
  { value: 6.5, label: 'UTC+06:30 (Myanmar)' },
  { value: 7, label: 'UTC+07:00' },
  { value: 8, label: 'UTC+08:00' },
  { value: 8.75, label: 'UTC+08:45 (Australia, Eucla)' },
  { value: 9, label: 'UTC+09:00' },
  { value: 9.5, label: 'UTC+09:30 (Australia, ACST)' },
  { value: 10, label: 'UTC+10:00' },
  { value: 10.5, label: 'UTC+10:30 (Lord Howe)' },
  { value: 11, label: 'UTC+11:00' },
  { value: 12, label: 'UTC+12:00' },
  { value: 12.75, label: 'UTC+12:45 (Chatham Islands)' },
  { value: 13, label: 'UTC+13:00' },
  { value: 14, label: 'UTC+14:00' },
];

export function TimezoneSelector({ value, onChange, disabled }: Props) {
  const current = (value ?? defaultUtcOffset());

  const handleChange = (e: SelectChangeEvent) => {
    onChange(Number(e.target.value));
  };

  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
      <FormControl size="small" sx={{ minWidth: 220 }}>
        <InputLabel id="tz-label">Timezone (UTC offset)</InputLabel>
        <Select
          labelId="tz-label"
          label="Timezone (UTC offset)"
          value={String(current)}
          onChange={handleChange}
          disabled={disabled}
          MenuProps={{ style: { maxHeight: 400 } }}
        >
          {OFFSETS.map(opt => (
            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <Button
        size="small"
        variant="text"
        onClick={() => onChange(defaultUtcOffset())}
        disabled={disabled}
      >
        Use my device time
      </Button>

      <Tooltip
        title="Times you enter below will be interpreted using this offset."
        arrow>
        <InfoIcon color="primary" fontSize="small" />
      </Tooltip>
    </Stack>
  );
}
